import { Fragment, ReactElement, ReactNode } from 'react';
import classnames from 'classnames';
import "./style.css";

interface VisiblePageProps {
    page: number
    total: number
    onChangePage: (page: number) => void
    isLoading: boolean
}

interface PaginationProps {
    pageCount: number
    pageIndex: number
    onChangePage: (page: number) => void
    isLoading: boolean
}

interface ButtonProps {
    icon?: ReactNode
    title?: string | number
    disabled?: boolean
    active?: boolean
    onClick?: () => void
}

const GetVisiblePages = ({ page, total, onChangePage, isLoading }: VisiblePageProps): ReactElement => {
    const filterPages = (visiblePages: number[], totalPages: number) => {
        return visiblePages.filter(page => page <= totalPages);
    }

    let pages: number[] = [];
    if (total > 0) {
        if (total < 7) {
            pages = filterPages([1, 2, 3, 4, 5, 6], total);
        } else {
            if (page % 5 >= 0 && page > 4 && page + 2 < total) {
                pages = [1, page - 1, page, page + 1, total];
            } else if (page % 5 >= 0 && page > 4 && page + 2 >= total) {
                pages = [1, total - 3, total - 2, total - 1, total];
            } else {
                pages = [1, 2, 3, 4, 5, total];
            }
        }
    }

    return (
        <>
            {
                pages.map((item, index, array) => (
                    <Fragment key={index.toString()}>
                        {
                            array[index - 1] + 2 <= item &&
                                <span className="flex h-9 w-7.5 items-center justify-center rounded py-1 px-4 font-medium">
                                    ...
                                </span>
                        }
                        <ButtonPagination onClick={() => onChangePage(item - 1)} title={item} active={page === item} disabled={isLoading} />
                    </Fragment>
                ))
            }
        </>
    )
}

function ButtonPagination({title, icon, onClick, disabled = false, active = false}: ButtonProps): ReactElement {
    return (
        <button
            className={classnames("flex items-center justify-center rounded ml-1 py-1.5 px-3.5 font-medium cursor-pointer disabled:cursor-default disabled:bg-transparent hover:bg-primary hover:text-white hover:disabled:text-black", { "active": active })}
            onClick={onClick} 
            disabled={disabled}
        >
            {icon && icon}
            {title && title}
        </button>
    )
}

export default function PaginationComponent ({ pageCount, pageIndex, onChangePage, isLoading }: PaginationProps): ReactElement {
    const canNextPage = pageIndex + 1 >= pageCount;
    const canPreviousPage = pageIndex <= 0;
    return (
        <>
            {
                pageCount > 1 &&
                    <div className="flex justify-start md:justify-end mt-2 mb-2 pagination-custom">
                        <ButtonPagination onClick={() => onChangePage(0)} disabled={isLoading || canPreviousPage} icon={<i className="fa-regular btn-icon fa-angle-double-left fa-sm" />}/>
                        <ButtonPagination onClick={() => onChangePage(pageIndex - 1)} disabled={isLoading || canPreviousPage} icon={<i className="fa-regular btn-icon fa-angle-left fa-sm" />}/>
                        <GetVisiblePages
                            key={pageIndex.toString() + pageCount.toString()}
                            page={pageIndex + 1}
                            total={pageCount}
                            onChangePage={(page) => onChangePage(page)}
                            isLoading={isLoading}
                        />
                        <ButtonPagination onClick={() => onChangePage(pageIndex + 1)} disabled={isLoading || canNextPage} icon={<i className="fa-regular btn-icon fa-angle-right fa-sm" />}/>
                        <ButtonPagination onClick={() => onChangePage(pageCount - 1)} disabled={isLoading || canNextPage} icon={<i className="fa-regular btn-icon fa-angle-double-right fa-sm" />}/>
                    </div>
            }
        </>
    );
}
